/**
 * batchJobsWs — single source of truth for the /ws/batch-jobs client URL.
 *
 * The WebSocket must follow the SAME base-resolution rule as HTTP API calls
 * (#233): runtime config first (an explicitly-empty API_BASE_URL means
 * "same-origin"), build-time Vite env second, current origin last. Building
 * the URL from import.meta.env directly pins one hostname into the bundle
 * and breaks every other hostname the deployment serves.
 *
 * Browsers cannot set headers on WebSocket upgrades (#234), so the credential
 * has to ride in the URL. It is never the session JWT: a URL leaks through
 * proxies, browser history and devtools (#3102). Each connect first asks the
 * API (with the normal Authorization header) for a short-lived, single-use
 * ticket bound to this user and storybook, then opens the socket with
 * `?ticket=`. Every reconnect needs a fresh ticket.
 */
import { resolveApiBaseUrl } from '../config/apiBaseUrl'
import { ApiClientError, apiClient } from './apiClient'

/** App-specific close codes emitted by the server's auth layer (#234). */
export const WS_CLOSE_UNAUTHENTICATED = 4401
export const WS_CLOSE_FORBIDDEN = 4403

/** True for close codes where reconnecting with the same credentials cannot succeed. */
export function isAuthCloseCode(code: number): boolean {
  return code === WS_CLOSE_UNAUTHENTICATED || code === WS_CLOSE_FORBIDDEN
}

/** True when a ticket request failed because the session itself is not allowed (retrying cannot help). */
export function isAuthTicketError(error: unknown): boolean {
  return error instanceof ApiClientError && (error.status === 401 || error.status === 403)
}

export function buildBatchJobsWsUrl(storybookId: string, ticket: string): string {
  const base = resolveApiBaseUrl() || window.location.origin
  const wsBase = base.replace(/^http/, 'ws') // http→ws, https→wss
  const params = new URLSearchParams({ storybookId, ticket })
  return `${wsBase}/ws/batch-jobs?${params.toString()}`
}

/** Ask the API for a one-time connection ticket for this storybook (#3102). */
export async function fetchBatchJobsWsTicket(storybookId: string): Promise<string> {
  const response = await apiClient.post<{ data?: { ticket?: string } }>('/api/batch-jobs/ws-ticket', { storybookId })
  const ticket = response?.data?.ticket
  if (!ticket) throw new Error('WebSocket ticket response did not include a ticket')
  return ticket
}

/**
 * Fetch a fresh ticket and open /ws/batch-jobs with it. Rejects if the ticket
 * request fails; callers treat that like a dropped connection (existing
 * reconnect/backoff), or stop when isAuthTicketError(error) is true.
 */
export async function openBatchJobsWs(storybookId: string): Promise<WebSocket> {
  const ticket = await fetchBatchJobsWsTicket(storybookId)
  return new WebSocket(buildBatchJobsWsUrl(storybookId, ticket))
}
